import { useState } from 'react';

/**
 * Format a phone number as user types it
 * @param phoneNumber The phone number string to format
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Format the phone number for display
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length === 0) return '';

  // Format as user types:
  if (digitsOnly.length <= 3) {
    return `(${digitsOnly}`;
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }
};

/**
 * Remove all non-digit characters from a phone number
 * @param formattedNumber The formatted phone number string
 * @returns Phone number with only digits
 */
export const unformatPhoneNumber = (formattedNumber: string): string => {
  return formattedNumber.replace(/\D/g, '');
};

/**
 * Prevent paste operation for sensitive fields that require re-entry
 * @param e Clipboard event
 */
export const preventPaste = (e: React.ClipboardEvent): void => {
  e.preventDefault();
};

/**
 * A hook that manages the state for an image/avatar/logo upload
 * @returns Image state and handlers
 */
export const useImageUpload = (initialImage?: string) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialImage ?? null);
  const [imageChanged, setImageChanged] = useState(false);

  const handleImageChange = (file: File) => {
    setImageFile(file);
    setImageChanged(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const resetImageState = () => {
    setImageChanged(false);
  };

  return {
    imageFile,
    imagePreview,
    imageChanged,
    handleImageChange,
    resetImageState,
  };
};
